class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]',
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

      // Enhanced Safari detection
     this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                    (navigator.userAgent.includes('AppleWebKit') && !navigator.userAgent.includes('Chrome')) ||
                    /iPad|iPhone|iPod/.test(navigator.userAgent);
                    
     // Add Safari class to body if detected
     if (this.isSafari) {
       document.body.classList.add('is-safari');
       this.setupSafariStickySearch();
       
       // Add special event listener for Safari focus issues
       this.input.addEventListener('focus', this.fixSafariSearch.bind(this));
       
       // Ensure search container is hidden on page load if search is empty
       if (this.predictiveSearchResults && !this.getQuery().length) {
         this.predictiveSearchResults.style.display = 'none';
         this.predictiveSearchResults.style.opacity = '0';
         this.predictiveSearchResults.style.visibility = 'hidden';
         this.predictiveSearchResults.style.height = '0';
         this.predictiveSearchResults.style.minHeight = '0';
         this.predictiveSearchResults.style.overflow = 'hidden';
         this.isOpen = false;
       }
       
       // For Safari, also add click handler to ensure check is performed when focusing input
       this.input.addEventListener('click', () => {
         if (!this.getQuery().length) {
           this.close(true);
           
           // Force hide the results container
           if (this.predictiveSearchResults) {
             this.predictiveSearchResults.style.display = 'none';
             this.predictiveSearchResults.style.opacity = '0';
             this.predictiveSearchResults.style.visibility = 'hidden';
             this.predictiveSearchResults.style.height = '0';
             this.predictiveSearchResults.style.minHeight = '0';
             this.predictiveSearchResults.style.overflow = 'hidden';
           }
         }
       });
     }
 
     this.isChrome = /chrome/i.test(navigator.userAgent) && !/edge|edg/i.test(navigator.userAgent);
     
     if (this.isChrome) {
       this.setupChromeSearch();
     }
 
     // Handle window orientation changes
     window.addEventListener('orientationchange', () => {
       if (this.isOpen) {
         // Delay adjustment to ensure dimensions are updated after orientation change
         setTimeout(() => {
           this.resetSearchContainerPosition();
         }, 100);
       }
     });

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
        // Add input event listener to catch all input changes including mobile keyboards
     this.input.addEventListener('input', () => {
       const currentSearchTerm = this.getQuery();
       if (!currentSearchTerm.length && this.predictiveSearchResults) {
         // Immediately hide the search results container
         this.predictiveSearchResults.style.display = 'none';
         this.predictiveSearchResults.style.opacity = '0';
         this.predictiveSearchResults.style.visibility = 'hidden';
         this.predictiveSearchResults.style.height = '0';
         this.predictiveSearchResults.style.minHeight = '0';
         this.predictiveSearchResults.style.overflow = 'hidden';
         
         // Also call close to ensure proper state
         this.close(true);
       }
     });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // Show container immediately when user starts typing
     if (this.predictiveSearchResults && this.searchTerm.length > 0) {
       // Set attribute to indicate we're loading results
       this.setAttribute('loading', true);
       
       // Show container immediately with a loading state
       this.predictiveSearchResults.innerHTML = '<div class="predictive-search-loading" style="padding: 20px; text-align: center;"><div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></div>';
       
       // Set container styles immediately
       this.predictiveSearchResults.style.display = 'block';
       this.predictiveSearchResults.style.opacity = '1';
       this.predictiveSearchResults.style.visibility = 'visible';
       this.predictiveSearchResults.style.height = 'auto';
       this.predictiveSearchResults.style.minHeight = '50px';
       
       // Force Safari-specific styles if needed
       if (this.isSafari) {
         this.style.position = 'relative';
         this.style.zIndex = '9999';
         this.adjustSafariSearchContainer();
       }
       
       // Set open state
       this.setAttribute('open', true);
       this.input.setAttribute('aria-expanded', true);
       this.isOpen = true;
     }
 
     // Then fetch actual search results
    
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]',
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item'),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
       // Get the current search term
     const currentSearchTerm = this.getQuery();
     
     // If search term is empty, don't display results
     if (!currentSearchTerm.length) {
       this.close(true);
       return;
     }
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    // Special handling for Safari
     if (this.isSafari) {
       // Force the dropdown to appear in Safari with full styling (no delay)
       this.predictiveSearchResults.style.display = 'block';
       this.predictiveSearchResults.style.opacity = '1';
       this.predictiveSearchResults.style.visibility = 'visible';
       this.predictiveSearchResults.style.maxHeight = '80vh';
       this.predictiveSearchResults.style.overflowY = 'auto';
       this.predictiveSearchResults.style.width = '100%';
       this.predictiveSearchResults.style.zIndex = '9999';
       this.predictiveSearchResults.style.position = 'absolute';
       this.predictiveSearchResults.style.top = '100%';
       this.predictiveSearchResults.style.left = '0';
       this.predictiveSearchResults.style.right = '0';
       this.predictiveSearchResults.style.boxSizing = 'border-box';
       this.predictiveSearchResults.style.webkitOverflowScrolling = 'touch';
       
       // Set z-index on the container element itself
       this.style.position = 'relative';
       this.style.zIndex = '9999';
       
       // Apply styles immediately (no delays)
       this.open();
       this.adjustSafariSearchContainer();
       this.fixSafariSearch();
     } else if (this.isChrome) {
       // Chrome-specific handling
       this.open();
       this.adjustChromeSearchContainer();
     } else {
       this.open();
     }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
        // Check if search term is empty
     const currentSearchTerm = this.getQuery();
     if (!currentSearchTerm.length) {
       this.close(true);
       return;
     }
   // Set the maxHeight for non-Safari browsers
     if (!this.isSafari) {
       this.predictiveSearchResults.style.maxHeight =
         this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
     }
       
     // Ensure visibility in Safari
     if (this.isSafari) {
       // Force display properties
       this.predictiveSearchResults.style.cssText = `
         display: block !important;
         opacity: 1 !important;
         visibility: visible !important;
         position: absolute !important;
         top: 100% !important;
         left: 0 !important;
         right: 0 !important;
         max-width: 100% !important;
         max-height: 80vh !important;
         overflow-y: auto !important;
         -webkit-overflow-scrolling: touch !important;
         z-index: 9999 !important;
         box-sizing: border-box !important;
         background-color: rgb(var(--color-background)) !important;
       `;
       
       // Set position on the container
       this.style.position = 'relative';
       this.style.zIndex = '9999';
       
       // Make sure to call adjustSafariSearchContainer to apply all necessary styles
       this.adjustSafariSearchContainer();
     }
       
     // Chrome-specific handling
     if (this.isChrome) {
       this.predictiveSearchResults.style.opacity = '1';
       this.predictiveSearchResults.style.visibility = 'visible';
       this.predictiveSearchResults.style.display = 'block';
       this.predictiveSearchResults.style.zIndex = '9999';
       this.style.zIndex = '9999';
       this.adjustChromeSearchContainer();
     } else if (!this.isSafari) { // For other browsers (not Chrome or Safari)
       this.predictiveSearchResults.style.zIndex = '9999';
       this.style.zIndex = '9999';
     }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
     // Apply position immediately without delay
     this.resetSearchContainerPosition();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
      if (this.predictiveSearchResults) {
       // First remove all styles
       this.predictiveSearchResults.removeAttribute('style');
       
       // Force a reflow
       void this.predictiveSearchResults.offsetHeight;
       
       // Then explicitly set styles to hide the container
       this.predictiveSearchResults.style.display = 'none';
       this.predictiveSearchResults.style.opacity = '0';
       this.predictiveSearchResults.style.visibility = 'hidden';
       this.predictiveSearchResults.style.height = '0';
       this.predictiveSearchResults.style.minHeight = '0';
       this.predictiveSearchResults.style.overflow = 'hidden';
     }
  }
  setupSafariStickySearch() {
     // Add special handling for Safari browsers
     window.addEventListener('resize', () => {
       if (this.isOpen) {
         this.adjustSafariSearchContainer();
       }
     });
   }
 
   adjustSafariSearchContainer() {
     if (!this.isSafari || !this.isOpen) return;
      // Get the current search term
     const currentSearchTerm = this.getQuery();
     
     // If search term is empty, don't display the results container in Safari
     if (!currentSearchTerm.length) {
       if (this.predictiveSearchResults) {
         this.predictiveSearchResults.style.display = 'none';
         this.predictiveSearchResults.style.opacity = '0';
         this.predictiveSearchResults.style.visibility = 'hidden';
       }
       return;
     }
     
     // Ensure the dropdown stays within container in Safari
     // First force position on the predictive-search component itself
     this.style.position = 'relative';
     this.style.zIndex = '9999';
     
     // Then force styles on the results container
     if (this.predictiveSearchResults) {
       // Save parent dimensions before touching styles
       const parentWidth = this.offsetWidth;
       const parentRect = this.getBoundingClientRect();
       
       // Force reset all styles first to avoid inheritance issues
       this.predictiveSearchResults.removeAttribute('style');
       
       // Force reflow to ensure clean slate
       void this.predictiveSearchResults.offsetHeight;
       
       // Apply each style individually and force them with !important
       this.predictiveSearchResults.style.cssText = `
         display: block !important;
         opacity: 1 !important;
         visibility: visible !important;
         position: absolute !important;
         top: 100% !important;
         left: 0 !important;
         right: 0 !important;
         width: ${parentWidth}px !important;
         max-width: 100% !important;
         max-height: 80vh !important;
         overflow-y: auto !important;
         -webkit-overflow-scrolling: touch !important;
         z-index: 9999 !important;
         box-sizing: border-box !important;
         background-color: rgb(var(--color-background)) !important;
         border-radius: var(--popup-corner-radius) !important;
         box-shadow: var(--popup-shadow-horizontal-offset)
           var(--popup-shadow-vertical-offset)
           var(--popup-shadow-blur-radius)
           rgba(var(--color-shadow), var(--popup-shadow-opacity)) !important;
       `;
       
       // Force reflow again to ensure styles are applied
       void this.predictiveSearchResults.offsetHeight;
       
       // Force each wrapper element inside to have correct styles
       const groupsWrapper = this.querySelector('.predictive-search__results-groups-wrapper');
       if (groupsWrapper) {
         groupsWrapper.style.cssText = `
           width: 100% !important;
           max-width: 100% !important;
           display: flex !important;
           flex-wrap: wrap !important;
         `;
         
         // Force styles on result groups
         const resultGroups = groupsWrapper.querySelectorAll('.predictive-search__result-group');
         resultGroups.forEach(group => {
           group.style.cssText = `
             width: 100% !important;
             max-width: 100% !important;
             flex-basis: 100% !important;
           `;
         });
       }
       
       // Force a final reflow to ensure everything renders
       void document.body.offsetHeight;
     }
   }
 
   setupChromeSearch() {
     // Add special handling for Chrome browsers
     window.addEventListener('resize', () => {
       if (this.isOpen) {
         this.adjustChromeSearchContainer();
       }
     });
 
     // Create a ResizeObserver to detect container size changes
     if (typeof ResizeObserver !== 'undefined') {
       this.resizeObserver = new ResizeObserver(() => {
         if (this.isOpen) {
           this.adjustChromeSearchContainer();
         }
       });
       this.resizeObserver.observe(this);
     }
     
     // Handle scroll events to ensure container stays positioned
     document.addEventListener('scroll', () => {
       if (this.isOpen) {
         this.adjustChromeSearchContainer();
       }
     }, { passive: true });
   }
 
   adjustChromeSearchContainer() {
     if (!this.isChrome || !this.isOpen) return;
     
     // Ensure the dropdown stays within container in Chrome
     requestAnimationFrame(() => {
       const parentWidth = this.offsetWidth;
       const parentRect = this.getBoundingClientRect();
       
       if (this.predictiveSearchResults) {
         // Apply explicit styles to ensure container is always properly positioned
         this.predictiveSearchResults.style.width = `${parentWidth}px`;
         this.predictiveSearchResults.style.maxWidth = '100%';
         this.predictiveSearchResults.style.left = '0';
         this.predictiveSearchResults.style.right = '0';
         this.predictiveSearchResults.style.position = 'absolute';
         this.predictiveSearchResults.style.top = '100%';
         this.predictiveSearchResults.style.zIndex = '9999';
         this.predictiveSearchResults.style.boxSizing = 'border-box';
         
         // Set z-index on the parent element too
         this.style.zIndex = '9999';
         
         // Force reflow
         void this.predictiveSearchResults.offsetHeight;
         
         const groupsWrapper = this.querySelector('.predictive-search__results-groups-wrapper');
         if (groupsWrapper) {
           groupsWrapper.style.width = '100%';
           groupsWrapper.style.maxWidth = '100%';
           groupsWrapper.style.display = 'flex';
           groupsWrapper.style.flexWrap = 'wrap';
         }
         
         // Check if container extends beyond viewport and adjust if needed
         const resultsRect = this.predictiveSearchResults.getBoundingClientRect();
         if (resultsRect.right > window.innerWidth) {
           const overflow = resultsRect.right - window.innerWidth;
           this.predictiveSearchResults.style.width = `${parentWidth - overflow - 10}px`;
         }
       }
     });
   }
 
   resetSearchContainerPosition() {
     if (!this.isOpen || !this.predictiveSearchResults) return;
     // Check if search term is empty
     const currentSearchTerm = this.getQuery();
     if (!currentSearchTerm.length) {
       if (this.predictiveSearchResults) {
         this.predictiveSearchResults.style.display = 'none';
         this.predictiveSearchResults.style.opacity = '0';
         this.predictiveSearchResults.style.visibility = 'hidden';
       }
       return;
     }
     
     // Reset all positioning styles first
     this.predictiveSearchResults.removeAttribute('style');
     
     // Force a reflow
     void this.predictiveSearchResults.offsetHeight;
     
     // Then reapply proper positioning
     if (this.isChrome) {
       this.adjustChromeSearchContainer();
     } else if (this.isSafari) {
       this.adjustSafariSearchContainer();
     } else {
       // Basic positioning for other browsers
       this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
       this.predictiveSearchResults.style.width = '100%';
       this.predictiveSearchResults.style.position = 'absolute';
       this.predictiveSearchResults.style.top = '100%';
       this.predictiveSearchResults.style.left = '0';
       this.predictiveSearchResults.style.right = '0';
       this.predictiveSearchResults.style.zIndex = '9999';
     }
 
     // Ensure z-index is applied to the container element
     this.style.zIndex = '9999';
   }
 
   // Add special Safari fix method
   fixSafariSearch() {
     if (!this.isSafari) return;
      // Check if search term is empty
     const currentSearchTerm = this.getQuery();
     if (!currentSearchTerm.length) {
       if (this.predictiveSearchResults) {
         this.predictiveSearchResults.style.display = 'none';
         this.predictiveSearchResults.style.opacity = '0';
         this.predictiveSearchResults.style.visibility = 'hidden';
       }
       return;
     }
     
  // Force Safari to properly display search results immediately
     if (this.predictiveSearchResults) {
       this.predictiveSearchResults.style.display = 'block';
       this.predictiveSearchResults.style.opacity = '1';
       this.predictiveSearchResults.style.visibility = 'visible';
       
       // Force reflow to ensure render
       void this.predictiveSearchResults.offsetHeight;
       
       // Apply styles immediately
       this.adjustSafariSearchContainer();
     }
   }
}

customElements.define('predictive-search', PredictiveSearch);
